import React, { useState, useEffect } from 'react';
import { OptionFlowAlert, OptionFlowResponse } from '../types';
import { apiService } from '../services/apiService';

interface FilterCriteria {
  ticker: string;
  type: 'all' | 'call' | 'put';
  minDte: number;
  minPremium: number;
  maxPremium: number;
  callSell: boolean;
  callBuy: boolean;
  putSell: boolean;
  putBuy: boolean;
}

export const OptionFilterFlow: React.FC = () => {
  const [alerts, setAlerts] = useState<OptionFlowAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filteredAlerts, setFilteredAlerts] = useState<OptionFlowAlert[]>([]);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  
  const [filters, setFilters] = useState<FilterCriteria>({
    ticker: '',
    type: 'all',
    minDte: 0,
    minPremium: 0,
    maxPremium: 10000000,
    callSell: false,
    callBuy: false,
    putSell: false,
    putBuy: false,
  });

  useEffect(() => {
    loadOptionFlow();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [alerts, filters]);

  const loadOptionFlow = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Single API call to get all data - backend handles caching and deduplication
      const data: OptionFlowResponse = await apiService.getOptionFlow({ limit: 1000 }, true);
      
      setAlerts(data.data);
      setLastRefreshTime(new Date());
      console.log(`Filter Flow data loaded at ${new Date().toLocaleTimeString()}:`, data.data.length, 'alerts');
    } catch (err) {
      setError('Failed to load option flow data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...alerts];

    // Filter by ticker
    if (filters.ticker) {
      filtered = filtered.filter(alert => 
        alert.ticker.toLowerCase().includes(filters.ticker.toLowerCase())
      );
    }

    // Filter by type
    if (filters.type !== 'all') {
      filtered = filtered.filter(alert => alert.type === filters.type);
    }

    // Filter by minimum DTE (days to expiry)
    if (filters.minDte > 0) {
      filtered = filtered.filter(alert => {
        const expiryDate = new Date(alert.expiry);
        const today = new Date();
        const daysToExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return daysToExpiry >= filters.minDte;
      });
    }

    // Filter by premium range
    if (filters.minPremium > 0) {
      filtered = filtered.filter(alert => {
        const premium = typeof alert.total_premium === 'string' ? parseFloat(alert.total_premium) : alert.total_premium;
        return premium >= filters.minPremium;
      });
    }

    if (filters.maxPremium < 10000000) {
      filtered = filtered.filter(alert => {
        const premium = typeof alert.total_premium === 'string' ? parseFloat(alert.total_premium) : alert.total_premium;
        return premium <= filters.maxPremium;
      });
    }

    // Filter by call/put sell/buy
    if (filters.callSell || filters.callBuy || filters.putSell || filters.putBuy) {
      filtered = filtered.filter(alert => {
        const bidPrem = typeof alert.total_bid_side_prem === 'string' ? parseFloat(alert.total_bid_side_prem) : alert.total_bid_side_prem;
        const askPrem = typeof alert.total_ask_side_prem === 'string' ? parseFloat(alert.total_ask_side_prem) : alert.total_ask_side_prem;
        
        if (alert.type === 'call') {
          if (filters.callSell && bidPrem > askPrem) return true;
          if (filters.callBuy && askPrem > bidPrem) return true;
          return false;
        } else if (alert.type === 'put') {
          if (filters.putSell && bidPrem > askPrem) return true;
          if (filters.putBuy && askPrem > bidPrem) return true;
          return false;
        }
        return false;
      });
    }

    setFilteredAlerts(filtered);
    console.log(`Applied filters: ${filtered.length} alerts match criteria`);
  };

  const handleFilterChange = (field: keyof FilterCriteria, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      ticker: '',
      type: 'all',
      minDte: 0,
      minPremium: 0,
      maxPremium: 10000000,
      callSell: false,
      callBuy: false,
      putSell: false,
      putBuy: false,
    });
  };

  const formatPremium = (premium: number | string) => {
    const numValue = typeof premium === 'string' ? parseFloat(premium) : premium;
    return `$${numValue.toLocaleString()}`;
  };

  const getTypeColor = (type: string) => {
    return type === 'call' ? 'type-badge-call' : 'type-badge-put';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading option flow data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadOptionFlow} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="option-filter-flow">
      <div className="filter-flow-header">
        <h2>Option Filter Flow</h2>
        <div className="filter-stats">
          <span>Total Alerts: {alerts.length}</span>
          <span>Filtered: {filteredAlerts.length}</span>
          {lastRefreshTime && (
            <span>Last Updated: {lastRefreshTime.toLocaleTimeString()}</span>
          )}
        </div>
      </div>

      {/* Filter Controls */}
      <div className="filter-controls">
        <div className="filter-section">
          <h3>Basic Filters</h3>
          <div className="filter-row">
            <div className="filter-field">
              <label htmlFor="ticker">Ticker Symbol</label>
              <input
                id="ticker"
                type="text"
                placeholder="e.g., AAPL, TSLA"
                value={filters.ticker}
                onChange={(e) => handleFilterChange('ticker', e.target.value)}
              />
            </div>
            <div className="filter-field">
              <label htmlFor="type">Option Type</label>
              <select
                id="type"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="call">Calls Only</option>
                <option value="put">Puts Only</option>
              </select>
            </div>
            <div className="filter-field">
              <label htmlFor="minDte">Min Days to Expiry</label>
              <input
                id="minDte"
                type="number"
                min="0"
                placeholder="0"
                value={filters.minDte === 0 ? '' : filters.minDte}
                onChange={(e) => handleFilterChange('minDte', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
        </div>

        <div className="filter-section">
          <h3>Premium Range</h3>
          <div className="filter-row">
            <div className="filter-field">
              <label htmlFor="minPremium">Minimum Premium</label>
              <input
                id="minPremium"
                type="number"
                min="0"
                placeholder="0"
                value={filters.minPremium === 0 ? '' : filters.minPremium}
                onChange={(e) => handleFilterChange('minPremium', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="filter-field">
              <label htmlFor="maxPremium">Maximum Premium</label>
              <input
                id="maxPremium"
                type="number"
                min="0"
                placeholder="10000000"
                value={filters.maxPremium === 10000000 ? '' : filters.maxPremium}
                onChange={(e) => handleFilterChange('maxPremium', parseInt(e.target.value) || 10000000)}
              />
            </div>
          </div>
        </div>

        <div className="filter-section">
          <h3>Buy/Sell Filters</h3>
          <div className="filter-row">
            <div className="filter-checkbox">
              <input
                id="callSell"
                type="checkbox"
                checked={filters.callSell}
                onChange={(e) => handleFilterChange('callSell', e.target.checked)}
              />
              <label htmlFor="callSell">Call Sell (Bid &gt; Ask)</label>
            </div>
            <div className="filter-checkbox">
              <input
                id="callBuy"
                type="checkbox"
                checked={filters.callBuy}
                onChange={(e) => handleFilterChange('callBuy', e.target.checked)}
              />
              <label htmlFor="callBuy">Call Buy (Ask &gt; Bid)</label>
            </div>
            <div className="filter-checkbox">
              <input
                id="putSell"
                type="checkbox"
                checked={filters.putSell}
                onChange={(e) => handleFilterChange('putSell', e.target.checked)}
              />
              <label htmlFor="putSell">Put Sell (Bid &gt; Ask)</label>
            </div>
            <div className="filter-checkbox">
              <input
                id="putBuy"
                type="checkbox"
                checked={filters.putBuy}
                onChange={(e) => handleFilterChange('putBuy', e.target.checked)}
              />
              <label htmlFor="putBuy">Put Buy (Ask &gt; Bid)</label>
            </div>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={clearFilters} className="clear-filters-button">
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Results Table */}
      <div className="filter-results">
        <h3>Filtered Results ({filteredAlerts.length} alerts)</h3>
        {filteredAlerts.length === 0 ? (
          <div className="no-results">
            <p>No alerts match the current filter criteria.</p>
          </div>
        ) : (
          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Type</th>
                  <th>Strike</th>
                  <th>Expiry</th>
                  <th>Premium</th>
                  <th>Buy Premium</th>
                  <th>Sell Premium</th>
                  <th>Underlying</th>
                  <th>Volume</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredAlerts.slice(0, 100).map((alert) => (
                  <tr key={alert.id}>
                    <td className="table-cell table-cell-primary">{alert.ticker}</td>
                    <td className="table-cell">
                      <span className={`type-badge ${getTypeColor(alert.type)}`}>
                        {alert.type.toUpperCase()}
                      </span>
                    </td>
                    <td className="table-cell table-cell-secondary">${alert.strike}</td>
                    <td className="table-cell table-cell-secondary">{alert.expiry}</td>
                    <td className="table-cell table-cell-primary">
                      {formatPremium(alert.total_premium)}
                    </td>
                    <td className="table-cell table-cell-secondary">
                      {formatPremium(alert.total_ask_side_prem)}
                    </td>
                    <td className="table-cell table-cell-secondary">
                      {formatPremium(alert.total_bid_side_prem)}
                    </td>
                    <td className="table-cell table-cell-secondary">
                      ${typeof alert.underlying_price === 'number' ? alert.underlying_price.toFixed(2) : alert.underlying_price || 'N/A'}
                    </td>
                    <td className="table-cell table-cell-secondary">
                      {alert.volume?.toLocaleString() || 'N/A'}
                    </td>
                    <td className="table-cell table-cell-secondary">
                      {new Date(alert.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredAlerts.length > 100 && (
              <div className="results-limit">
                <p>Showing first 100 results of {filteredAlerts.length} total matches.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
